import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  Check,
  Clock,
  Flag,
  Play,
  RotateCcw,
  Shuffle,
  Timer,
  X,
} from "lucide-react";

/* ---------------- Fisher-Yates shuffle w/ seed ---------------- */
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}
function shuffle(arr, seed) {
  const out = arr.slice();
  const rand = seededRandom(seed);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/* AFTR = 100 questions / 180 minutes / 70% pass */
const EXAM_MINUTES = 180;

export default function AfspExamSim() {
  const { user, loading } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();

  const [status, setStatus] = useState(null);
  const [questions, setQuestions] = useState(null); // shuffled
  const [answers, setAnswers] = useState({});       // {questionId: shuffledOptionIndex}
  const [flagged, setFlagged] = useState({});       // {questionId: true}
  const [seed, setSeed] = useState(null);
  const [started, setStarted] = useState(false);
  const [startTs, setStartTs] = useState(null);
  const [timeLeft, setTimeLeft] = useState(EXAM_MINUTES * 60);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/afsp-exam-sim"); return; }
    api.get("/afsp/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ has_access: false }));
  }, [user, loading, nav]);

  /* Fetch + shuffle when the user clicks Start. */
  const startExam = async () => {
    try {
      const r = await api.get("/afsp/exam-sim");
      const s = Math.floor(Math.random() * 1_000_000) + 1;
      // Shuffle question order AND each question's options.
      const shuffledQs = shuffle(r.data.questions, s).map((q, i) => {
        const optionOrder = shuffle(
          q.options.map((opt, idx) => ({ opt, originalIdx: idx })),
          s + i + 1,
        );
        return {
          ...q,
          shuffledOptions: optionOrder,
        };
      });
      setSeed(s);
      setQuestions(shuffledQs);
      setStarted(true);
      setStartTs(Date.now());
      setTimeLeft(EXAM_MINUTES * 60);
      setAnswers({}); setFlagged({}); setCurrent(0); setResult(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start exam");
    }
  };

  /* Countdown ticker. */
  useEffect(() => {
    if (!started || result) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          submitExam(true); // autosubmit
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [started, result, submitExam]);

  /* Warn on unload / route away during an active exam. */
  useEffect(() => {
    if (!started || result) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [started, result]);

  const answeredCount = Object.keys(answers).length;
  const flaggedCount = Object.values(flagged).filter(Boolean).length;
  const progressPct = questions ? Math.round((answeredCount / questions.length) * 100) : 0;

  const pick = (qid, shuffledOptIdx) => setAnswers((prev) => ({ ...prev, [qid]: shuffledOptIdx }));
  const toggleFlag = (qid) => setFlagged((prev) => ({ ...prev, [qid]: !prev[qid] }));

  const submitExam = useCallback(async (autosubmit = false) => {
    if (!questions || result) return;
    setSubmitting(true);
    /* Map shuffled selection → original option index so server can grade. */
    const originalAnswers = {};
    for (const [qid, shuffledIdx] of Object.entries(answers)) {
      const q = questions.find((x) => x.id === qid);
      if (q && q.shuffledOptions[shuffledIdx]) {
        originalAnswers[qid] = q.shuffledOptions[shuffledIdx].originalIdx;
      }
    }
    const durationSec = startTs ? Math.round((Date.now() - startTs) / 1000) : 0;
    try {
      const r = await api.post("/afsp/exam-sim/submit", {
        answers: originalAnswers,
        duration_seconds: durationSec,
        autosubmit: autosubmit,
      });
      setResult(r.data);
      clearInterval(timerRef.current);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (autosubmit) toast.warning("Time expired — your exam was auto-submitted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  }, [questions, result, answers, startTs]);

  const resetExam = () => {
    setStarted(false); setResult(null); setQuestions(null);
    setAnswers({}); setFlagged({}); setCurrent(0);
    setTimeLeft(EXAM_MINUTES * 60);
  };

  /* ---------------- Render ---------------- */
  if (loading || !status) return <div className="p-10 text-muted-foreground">Loading…</div>;

  if (!status.has_access) {
    return (
      <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="exam-sim-locked">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-accent/10 text-accent grid place-items-center mb-4">
            <Award size={26} />
          </div>
          <h2 className="font-serif text-3xl mb-2">Unlock AFSP Prep</h2>
          <p className="text-sm text-muted-foreground mb-6">
            The 100-question timed AFTR simulation is part of the AFSP Prep Bundle ($199, one-time).
          </p>
          <Button asChild className="w-full"><Link to="/afsp-bundle">See the bundle</Link></Button>
        </Card>
      </div>
    );
  }

  /* --- START SCREEN --- */
  if (!started && !result) {
    return (
      <div className="min-h-screen bg-background" data-testid="exam-sim-start">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto p-6 flex items-center justify-between">
            <Link to="/afsp-prep" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="exam-sim-back">
              <ArrowLeft size={14} /> Back to AFSP Prep
            </Link>
            <div className="font-serif text-xl">{branding.brand_name}</div>
          </div>
        </header>
        <div className="max-w-3xl mx-auto p-6 lg:p-12">
          <div className="label-caps mb-3 text-primary flex items-center gap-2">
            <Timer size={14} /> Progressive Practice Mode
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mb-4">Full AFTR Simulation</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            100 questions. 3 hours on the clock. 70% to pass — same rules the real IRS-approved AFTR uses.
            Question order and answer choices are randomized every time so you can retake without memorizing the sequence.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { icon: <Shuffle size={22} />, label: "100 questions", sub: "shuffled" },
              { icon: <Timer size={22} />, label: "3 hours", sub: "autosubmit at 0:00" },
              { icon: <Check size={22} />, label: "70% pass", sub: "just like the real AFTR" },
              { icon: <Award size={22} />, label: "Certificate", sub: "issued on first pass" },
            ].map((c, i) => (
              <Card key={i} className="p-4 text-center">
                <div className="mx-auto h-11 w-11 rounded-md bg-primary/10 text-primary grid place-items-center mb-3">{c.icon}</div>
                <div className="font-serif text-lg leading-none">{c.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.sub}</div>
              </Card>
            ))}
          </div>

          <Card className="p-6 bg-amber-50/40 border-amber-500/30 dark:bg-amber-950/20 mb-8">
            <div className="font-serif text-lg mb-2">Before you start</div>
            <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-4">
              <li>Close other tabs — leaving this page mid-exam warns you but does NOT save progress.</li>
              <li>You can flag questions to revisit — the review panel highlights them.</li>
              <li>Autosubmit fires when the timer hits 0:00. Unanswered questions count as wrong.</li>
              <li>Your best attempt is saved. Passing (70%+) here counts toward your certificate.</li>
            </ul>
          </Card>

          <Button size="lg" onClick={startExam} className="w-full text-base" data-testid="exam-sim-start-btn">
            <Play size={16} className="mr-2" /> Start the 3-hour AFTR simulation
          </Button>
        </div>
      </div>
    );
  }

  /* --- RESULT SCREEN --- */
  if (result) {
    return (
      <div className="min-h-screen bg-background" data-testid="exam-sim-result">
        <div className="max-w-4xl mx-auto p-6 lg:p-12">
          <Card className={`p-8 mb-8 border-2 ${result.passed ? "border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-amber-500/50 bg-amber-50/40 dark:bg-amber-950/20"}`}>
            <div className="flex flex-wrap items-center gap-5">
              <div className={`h-16 w-16 rounded-full grid place-items-center shrink-0 ${result.passed ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>
                {result.passed ? <Award size={30} /> : <RotateCcw size={30} />}
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="label-caps mb-1">AFTR Simulation Result</div>
                <h2 className="font-serif text-4xl leading-tight">{result.score}/{result.total} · {result.percent}%</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {result.passed
                    ? "You cleared the 70% AFTR threshold under full exam pressure. Grab your certificate below."
                    : "Below the 70% AFTR passing threshold. Review the domain breakdown to see where to focus, then retake."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.passed && (
                  <Button asChild data-testid="exam-sim-cert-btn">
                    <Link to="/afsp-certificate"><Award size={14} className="mr-2" /> View certificate</Link>
                  </Button>
                )}
                <Button variant="outline" onClick={resetExam} data-testid="exam-sim-retake-btn">
                  <RotateCcw size={14} className="mr-2" /> Retake
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-6 mb-8" data-testid="exam-sim-breakdown">
            <div className="label-caps mb-3">Domain breakdown</div>
            <div className="space-y-3">
              {result.domain_breakdown.map((d, i) => {
                const pct = d.total ? Math.round((d.correct / d.total) * 100) : 0;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{d.domain}</span>
                      <span className="text-muted-foreground">{d.correct}/{d.total} · {pct}%</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="space-y-3">
            {result.detail.filter(d => !d.is_correct).slice(0, 15).map((d, i) => (
              <Card key={d.id} className="p-5 border-red-200 dark:border-red-950/40" data-testid={`exam-sim-miss-${i}`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-serif text-red-500 text-lg">×</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.domain}</span>
                </div>
                <p className="text-sm font-medium mb-3">{d.prompt}</p>
                <div className="text-xs text-muted-foreground mb-1">Correct answer: <strong className="text-emerald-600">{d.options[d.correct]}</strong></div>
                <div className="text-xs text-muted-foreground mb-3">Your answer: <strong>{typeof d.selected === "number" ? d.options[d.selected] : "— skipped —"}</strong></div>
                <div className="p-3 bg-secondary/60 rounded-md text-xs leading-relaxed">{d.rationale}</div>
              </Card>
            ))}
            {result.detail.filter(d => !d.is_correct).length > 15 && (
              <p className="text-xs text-center text-muted-foreground">Showing your first 15 misses. All answers were graded — retake to improve.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* --- EXAM IN PROGRESS --- */
  const q = questions[current];
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const timerCls = timeLeft <= 300 ? "text-red-500" : timeLeft <= 900 ? "text-amber-500" : "text-foreground";

  return (
    <div className="min-h-screen bg-background" data-testid="exam-sim-live">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className={timerCls} />
            <span className={`font-mono text-2xl ${timerCls}`} data-testid="exam-sim-timer">
              {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
            </span>
          </div>
          <div className="flex-1 min-w-[160px]">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              <span>Progress</span>
              <span>{answeredCount}/{questions.length}</span>
            </div>
            <Progress value={progressPct} className="h-1.5" />
          </div>
          <div className="text-xs text-muted-foreground">
            {flaggedCount} flagged
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowReview((v) => !v)}
            data-testid="exam-sim-review-btn"
          >
            {showReview ? "Hide review" : "Review panel"}
          </Button>
          <Button
            size="sm"
            onClick={() => submitExam(false)}
            disabled={submitting}
            data-testid="exam-sim-submit-btn"
          >
            {submitting ? "Submitting…" : "Submit exam"}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        {/* Review panel */}
        {showReview && (
          <Card className="p-4 mb-6" data-testid="exam-sim-review-panel">
            <div className="label-caps mb-3">Jump to a question</div>
            <div className="grid grid-cols-10 gap-1.5">
              {questions.map((qq, i) => {
                const isAns = qq.id in answers;
                const isFlag = flagged[qq.id];
                const cls = isFlag
                  ? "bg-amber-500 text-white"
                  : isAns
                    ? "bg-emerald-500 text-white"
                    : "bg-secondary text-muted-foreground";
                return (
                  <button
                    key={qq.id}
                    onClick={() => { setCurrent(i); setShowReview(false); }}
                    className={`h-8 text-xs font-medium rounded ${cls} ${i === current ? "ring-2 ring-primary" : ""}`}
                    data-testid={`exam-sim-jump-${i}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-muted-foreground uppercase tracking-widest">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> Answered</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-500" /> Flagged</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-secondary" /> Blank</span>
            </div>
          </Card>
        )}

        {/* Question */}
        <Card className="p-6 mb-6" data-testid="exam-sim-question">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-primary text-3xl">Q{current + 1}</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">of {questions.length} · {q.domain}</span>
            </div>
            <Button
              variant={flagged[q.id] ? "default" : "outline"}
              size="sm"
              onClick={() => toggleFlag(q.id)}
              data-testid="exam-sim-flag-btn"
            >
              <Flag size={13} className="mr-1.5" />
              {flagged[q.id] ? "Flagged" : "Flag for review"}
            </Button>
          </div>
          <p className="font-medium text-lg mb-5 mt-3">{q.prompt}</p>
          <div className="space-y-2">
            {q.shuffledOptions.map((so, oi) => {
              const isSel = answers[q.id] === oi;
              return (
                <button
                  key={oi}
                  type="button"
                  onClick={() => pick(q.id, oi)}
                  className={`w-full text-left p-3 rounded-md border-2 transition-colors flex items-center gap-3 ${isSel ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  data-testid={`exam-sim-opt-${oi}`}
                >
                  <span className={`h-7 w-7 rounded-full grid place-items-center text-xs shrink-0 ${isSel ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {String.fromCharCode(65 + oi)}
                  </span>
                  <span className="text-sm">{so.opt}</span>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Nav */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0}
            data-testid="exam-sim-prev-btn"
          >
            <ArrowLeft size={14} className="mr-2" /> Previous
          </Button>
          <div className="text-xs text-muted-foreground">
            Question <strong className="text-foreground">{current + 1}</strong> / {questions.length}
          </div>
          <Button
            onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
            disabled={current === questions.length - 1}
            data-testid="exam-sim-next-btn"
          >
            Next <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
